import React, { useState } from 'react';
import { X } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import IndiaMap from './IndiaMap';
import { nationalKPIs, statesData } from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-bold' :
  pr >= 65 ? 'text-amber-600 font-bold' :
             'text-red-600 font-bold';

function DetailRow({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-text-tertiary">{label}</span>
      <span className={`text-xs font-semibold text-text-primary ${valueClass ?? ''}`}>{value}</span>
    </div>
  );
}

export default function NationalOverview() {
  const [selectedState, setSelectedState] = useState<string | null>(null);

  const stateData = selectedState
    ? statesData.find(s => s.name === selectedState) ?? null
    : null;

  // Derived values for detail panel
  const panelData = stateData
    ? {
        title: stateData.name,
        totalPlants: stateData.plants.toLocaleString(),
        capacity: `${stateData.capacityGWp.toFixed(2)} GWp`,
        generation: `${stateData.generationGWh.toFixed(2)} GWh`,
        specificYield: `${(stateData.generationGWh / stateData.capacityGWp).toFixed(2)} kWh/kWp`,
        avgPR: stateData.prPercent,
        avgCUF: `${stateData.cufPercent.toFixed(1)}%`,
        availability: `${stateData.activePercent.toFixed(0)}%`,
        activePlants: Math.round(stateData.plants * stateData.activePercent / 100).toLocaleString(),
        offlinePlants: Math.round(stateData.plants * (1 - stateData.activePercent / 100)).toLocaleString(),
        offlineIsRed: stateData.activePercent < 95,
        activeAlarms: stateData.alarms.toLocaleString(),
        alarmsIsRed: stateData.alarms > 100,
      }
    : {
        title: 'National Summary',
        totalPlants: nationalKPIs.totalPlants.toLocaleString(),
        capacity: `${nationalKPIs.installedCapacityGWp} GWp`,
        generation: `${nationalKPIs.todayGenerationGWh} GWh`,
        specificYield: `${nationalKPIs.avgSpecificYield} kWh/kWp`,
        avgPR: nationalKPIs.avgPR,
        avgCUF: `${nationalKPIs.avgCUF}%`,
        availability: `${(statesData.reduce((s, d) => s + d.plants * d.activePercent, 0) / statesData.reduce((s, d) => s + d.plants, 0)).toFixed(1)}%`,
        activePlants: nationalKPIs.activePlants.toLocaleString(),
        offlinePlants: nationalKPIs.offlineFault.toLocaleString(),
        offlineIsRed: true,
        activeAlarms: nationalKPIs.activeAlarms.toLocaleString(),
        alarmsIsRed: true,
      };

  // National avg specific yield for secondary cards
  const avgAvailability = (
    statesData.reduce((s, d) => s + d.plants * d.activePercent, 0) /
    statesData.reduce((s, d) => s + d.plants, 0)
  ).toFixed(1);

  return (
    <div className="p-6 space-y-6">
      {/* ── Primary KPIs — 6 cards ── */}
      <div className="flex gap-4">
        <KPICard title="Total Plants"          value="28,450"                           subtext="+342 this month"       subtextColor="green" />
        <KPICard title="Installed Capacity"    value="9.1 GWp"                          subtext="Across 28 states" />
        <KPICard title="Today's Generation"    value="36 GWh"                           subtext="+6.2% vs yesterday"    subtextColor="green" />
        <KPICard title="Active Plants"         value="24,318"                           subtext="85.4% of total"        subtextColor="green" />
        <KPICard title="Offline / Fault"       value="4,132"                            subtext="14.6% needs review"    subtextColor="red"   bgColor="bg-red-50" />
        <KPICard title="Avg Performance Ratio" value={`${nationalKPIs.avgPR}%`}         subtext="Good zone (>72%)"      subtextColor="green" />
      </div>

      {/* ── Two-column: Map + Detail Panel ── */}
      <div className="flex gap-4" style={{ minHeight: 460 }}>
        {/* Left: Heatmap (65%) */}
        <div className="bg-white rounded-card border border-border shadow-card p-4" style={{ flex: '0 0 65%' }}>
          <SectionDivider title="Generation Heatmap — India" subtitle="Click a state to view details" />
          <div style={{ height: 400 }}>
            <IndiaMap
              onStateSelect={setSelectedState}
              selectedState={selectedState}
            />
          </div>
        </div>

        {/* Right: Detail Panel (35%) */}
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex flex-col" style={{ flex: '0 0 35%' }}>
          {/* Panel header */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-text-primary">{panelData.title}</p>
            {selectedState && (
              <button
                onClick={() => setSelectedState(null)}
                className="p-1 rounded hover:bg-bg-secondary text-text-tertiary hover:text-text-primary transition-colors"
                title="Reset to national view"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {selectedState && (
            <p className="text-xs text-text-tertiary mb-3 -mt-1">
              Click ✕ to return to national view
            </p>
          )}

          {/* 10 label-value pairs */}
          <div className="flex-1 space-y-0">
            <DetailRow label="Total Plants"        value={panelData.totalPlants} />
            <DetailRow label="Installed Capacity"  value={panelData.capacity} />
            <DetailRow label="Today's Generation"  value={panelData.generation} />
            <DetailRow label="Specific Yield"      value={panelData.specificYield} />
            <DetailRow
              label="Avg PR"
              value={`${panelData.avgPR}%`}
              valueClass={prCls(panelData.avgPR)}
            />
            <DetailRow label="Avg CUF"             value={panelData.avgCUF} />
            <DetailRow label="Plant Availability"  value={panelData.availability} />
            <DetailRow label="Active Plants"       value={panelData.activePlants} />
            <DetailRow
              label="Offline Plants"
              value={panelData.offlinePlants}
              valueClass={panelData.offlineIsRed ? 'text-red-600' : ''}
            />
            <DetailRow
              label="Active Alarms"
              value={panelData.activeAlarms}
              valueClass={panelData.alarmsIsRed ? 'text-red-600' : ''}
            />
          </div>
        </div>
      </div>

      {/* ── Secondary KPI row: Avg CUF · Avg Specific Yield · Plant Availability ── */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg CUF Today</p>
          <p className="text-xl font-bold text-text-primary mt-1">{nationalKPIs.avgCUF}%</p>
          <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg Specific Yield</p>
          <p className="text-xl font-bold text-text-primary mt-1">{nationalKPIs.avgSpecificYield} kWh/kWp</p>
          <p className="text-xs text-text-secondary mt-1">National average · today</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Plant Availability</p>
          <p className="text-xl font-bold text-text-primary mt-1">{avgAvailability}%</p>
          <p className="text-xs text-text-secondary mt-1">Solar hours basis</p>
        </div>
      </div>
    </div>
  );
}
